import { apiClient } from '@/api/client';
import {
  API_STUDENT_JOIN_OFFERS,
  API_STUDENT_JOIN_OFFERS_REMIND_LATER,
} from '@/api/endpoints';
import { STUDENT_JOIN_OFFERS_QUERY_KEY } from '@/api/queryKeys';
import { Button } from '@/components/Button';
import { StudentJoinCard } from '@/components/Cards/StudentJoinCard';
import { Icon } from '@/components/Icons';
import { EKlaseLogo } from '@/components/Logo';
import { LogoutConfirmDialog } from '@/components/LogoutConfirmDialog';
import { AddStudentOfferModal } from '@/components/Modal/AddStudentOfferModal';
import { DeclineStudentOfferModal } from '@/components/Modal/DeclineStudentOfferModal';
import { AddStudentErrorModal } from '@/components/Modal/AddStudentErrorModal';
import { Snackbar } from '@/components/Snackbar';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { STORED_OFFERS_SKIPPED_SESSION } from '@/auth/stored';
import { ROUTE_HOME } from '@/router/routes';
import { ButtonVariant, IconType, Size } from '@/types/components';
import type { StudentJoinOfferResponse } from '@/types/user';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const StudentJoinOffers: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [declineOfferId, setDeclineOfferId] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isAddOfferModalOpen, setIsAddOfferModalOpen] = useState(false);
  const [isAddStudentErrorModalOpen, setIsAddStudentErrorModalOpen] =
    useState(false);

  const { data: joinOffers, isFetching } = useQuery<StudentJoinOfferResponse[]>(
    {
      queryKey: STUDENT_JOIN_OFFERS_QUERY_KEY,
      queryFn: () =>
        apiClient
          .get<StudentJoinOfferResponse[]>(API_STUDENT_JOIN_OFFERS)
          .then(unwrapAxiosResponse),
      staleTime: Infinity,
    },
  );

  const { mutateAsync: remindLater, isPending } = useMutation({
    mutationFn: () => apiClient.post(API_STUDENT_JOIN_OFFERS_REMIND_LATER),
    onSuccess: () =>
      queryClient.setQueryData(STUDENT_JOIN_OFFERS_QUERY_KEY, []),
  });

  const handleSkip = async () => {
    if (isPending) {
      return;
    }

    try {
      await remindLater();
      sessionStorage.setItem(STORED_OFFERS_SKIPPED_SESSION, 'true');
      navigate(ROUTE_HOME, { replace: true });
    } catch (error) {
      logRequestError(ErrorMessage.RemindLaterStudentJoinOffers, error);
    }
  };

  return (
    <div className="min-h-screen bg-background md:flex md:flex-col md:items-center md:bg-info-surface md:px-8 md:pt-14 md:pb-8">
      <EKlaseLogo className="hidden shrink-0 text-primary md:mb-16 md:block" />
      <div className="flex h-screen flex-col md:h-auto md:max-h-[620px] md:min-h-[440px] md:w-[360px] md:overflow-hidden md:rounded-xl md:bg-card md:shadow-modal">
        <header className="flex shrink-0 flex-col gap-2 px-5 pt-[calc(40px+env(safe-area-inset-top))] pb-4 md:hidden">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-title-30 text-foreground">
              {t('offers.pageTitleMobile')}
            </h1>
            <button
              type="button"
              onClick={() => setIsLogoutConfirmOpen(true)}
              aria-label={t('global.logout')}
              className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
            >
              <Icon icon={IconType.Logout} />
            </button>
          </div>
          <p className="text-body-14 text-muted-foreground">
            {t('offers.description')}
          </p>
        </header>

        <header className="hidden shrink-0 items-center justify-between gap-3 px-5 pt-5 pb-4 md:flex">
          <h1 className="text-headline-20 whitespace-nowrap text-foreground">
            {t('offers.pageTitleWeb')}
          </h1>
          <button
            type="button"
            onClick={() => setIsLogoutConfirmOpen(true)}
            aria-label={t('global.logout')}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
          >
            <Icon icon={IconType.Logout} />
          </button>
        </header>

        <div className="relative flex flex-1 flex-col gap-3 overflow-y-auto px-4 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] md:px-5 [&::-webkit-scrollbar]:hidden">
          {(joinOffers ?? []).map((offer) => (
            <StudentJoinCard
              key={offer.userPublicId}
              firstName={offer.firstName}
              lastName={offer.lastName}
              className={offer.className}
              schoolName={offer.schoolName}
              onStudentAdd={() => setIsAddOfferModalOpen(true)}
              onStudentRemove={() => setDeclineOfferId(offer.userPublicId)}
            />
          ))}
          {isFetching && (
            <div className="absolute inset-0 flex items-center justify-center">
              <PageSpinner />
            </div>
          )}
        </div>

        <footer className="flex shrink-0 flex-col items-center border-t border-border bg-background px-4 pt-4 pb-[calc(32px+env(safe-area-inset-bottom))] md:border-t-0 md:bg-card md:px-5 md:pt-0 md:pb-5">
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.Large}
            onClick={handleSkip}
            isLoading={isPending}
            className="w-full"
          >
            {t('offers.skip')}
          </Button>
        </footer>
      </div>

      {!!declineOfferId && (
        <DeclineStudentOfferModal
          userPublicId={declineOfferId}
          onSuccess={() => setShowSnackbar(true)}
          onClose={() => setDeclineOfferId(null)}
        />
      )}

      {isAddOfferModalOpen && (
        <AddStudentOfferModal
          isOpen={isAddOfferModalOpen}
          onSuccess={() => setShowSnackbar(true)}
          onNotFound={() => setIsAddStudentErrorModalOpen(true)}
          onClose={() => setIsAddOfferModalOpen(false)}
        />
      )}

      {isAddStudentErrorModalOpen && (
        <AddStudentErrorModal
          isOpen={isAddStudentErrorModalOpen}
          onClose={() => setIsAddStudentErrorModalOpen(false)}
        />
      )}

      <Snackbar
        message={t('offers.changesSavedSnackbar')}
        isOpen={showSnackbar}
        onDismiss={() => setShowSnackbar(false)}
      />
      <LogoutConfirmDialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
      />
    </div>
  );
};
